import React, { useMemo, useCallback } from "react";
import PropTypes from 'prop-types';
import moment from "moment";
import cx from "classnames";
import { Typography, Icon } from "@mui/material";
import { useIntl } from "react-intl";
import MessageBubble from "Modules/chat/components/MessageBubble";
import MessageBubbleGroup from "Modules/chat/components/MessageBubbleGroup";
import { PARTICIPANT_ROLE, MESSAGE_STATUS } from '../types/constants';
import { linkifyText } from '../utils/linkifyText';

// Date labels for grouping messages
const DATE_LABELS = {
  sameDay: "[Today]",
  nextWeek: "dddd",
  lastDay: "[Yesterday]",
  lastWeek: "[Last] dddd",
  sameElse: "D MMMM YYYY",
};

/**
 * Determine if message is from current user to build groups
 * TODO: call this during group build
 */
const isSelfMessage = (message, authId, salesforceEnabled) => {
  if (!salesforceEnabled) {
    return message.sender === authId;
  }
  
  // Salesforce: Check multiple properties
  return message.sender === 'user' || 
         message.sender === authId || 
         message.senderRole === PARTICIPANT_ROLE.END_USER || 
         message.isEndUserMessage === true ||
         message.actorType === PARTICIPANT_ROLE.END_USER;
};

/**
 * Format message timestamp
 */
const formatTimestamp = (message, locale) => {
  const date = message.createdOn || message.timestamp;
  if (!date) return "";

  const localDate = moment(date).local();
  const timestampLabel = localDate
    ? `${moment(localDate).locale(locale).format("h:mm a").replace("pm", "p.m").replace("am", "a.m")}`
    : '';
  
  try {
    return timestampLabel;
  } catch (error) {
    console.error('[Salesforce:Message] Invalid date:', error);
    return "";
  }
};

/**
 * Build senderInfo for the current group
 * TODO: call this during starting a new group to build the showing info
 * @returns 
 */
const getSenderInfo = (isFromSelf, chatUsers, message) => {
  if (isFromSelf) return null;

  // TODO: Keep for check, chatUsers do not contain Support agent
  // const user = chatUsers.find((u) => u.id === message.sender);

  // if (user) {
  //   return user;
  // }

  // conversationEntry is only available for current conversation; originalEntry only available for history messages (not overlap)
  const supportAgentName = message.conversationEntry?.senderDisplayName
  || message.originalEntry?.senderDisplayName
  || 'Support Agent';

  return {
    id: 'support-agent',
    label: supportAgentName,
    name: supportAgentName,
    avatarFile: null,
    lastConnected: new Date().toISOString(), // TODO: this should be the param for entire message list, not for each group
  };
};

/**
 * Get message text from various possible fields
 */
const getMessageText = (message) => {
  return message.message || message.text || message.messageText || "";
};

const SupportChatMessages = ({
  translated,
  items = [],
  authId,
  chatUsers = [],
  salesforceEnabled = false,
  hasAgentJoined = false,
  agentJoinedAt = null,
  agentName = null,
  agentJoinedMessage = '',
}) => {
  const { locale } = useIntl();

  // Memoize first message date
  const firstMessageDate = useMemo(() => {
    if (!items || items.length === 0) return null;
    const sortedItems = [...items].sort((a, b) => {
      const timeA = a.timestamp || a.createdOn || 0;
      const timeB = b.timestamp || b.createdOn || 0;
      return timeA - timeB;
    });
    const firstTimestamp = sortedItems[0]?.timestamp || sortedItems[0]?.createdOn;
    return firstTimestamp ? moment(firstTimestamp).local().format("YYYY-MM-DD") : null;
  }, [items]);

  // Memoize grouped and sorted messages
  const groupedMessages = useMemo(() => {
    if (!items || !Array.isArray(items)) { // Safety check
      return [];
    }

    const allItems = [...items];

    // check to push agent joined item to items array
    if (hasAgentJoined && agentJoinedAt){
      allItems.push({
        _type: 'agent_joined',
        timestamp: agentJoinedAt,
        message: agentJoinedMessage,
      });
    }

    // Update date labels with localized strings
    const dateLocaleLabel = {
      ...DATE_LABELS,
      sameDay: `[${translated?.["date_today"] || "Today"}]`,
      lastDay: `[${translated?.["date_yesterday"] || "Yesterday"}]`,
      lastWeek: `[${translated?.["last_day"] || "Last"}] dddd`,
    };

    // Group messages by date (sorting will happen per date group below)
    const messageGroupsByDate = allItems.reduce((acc, item) => {
      const timestamp = item.timestamp || item.createdOn;
      if (!timestamp) return acc;

      const localDate = moment(timestamp).local().format("YYYY-MM-DD");
      if (!acc[localDate]) {
        acc[localDate] = [];
      }
      acc[localDate].push(item);
      return acc;
    }, {});

    // Sort date keys and process groups
    const result = Object.keys(messageGroupsByDate)
    .sort((a, b) => new Date(a) - new Date(b))
    .map((dateKey) => {
      // Sort messages within this date group
      const sortedMessages = [...messageGroupsByDate[dateKey]].sort((a, b) => {
        const timeA = a.timestamp || a.createdOn || 0;
        const timeB = b.timestamp || b.createdOn || 0;
        return timeA - timeB;
      });

      // Group messages by sender (consecutive), but keep agent_joined as separate item
      const messagesBySender = [];
      let currentGroup = null;

      sortedMessages.forEach(message => {
        // Handle "agent joined" pseudo-message separately
        if (message._type === 'agent_joined') {
          if (currentGroup) {
            messagesBySender.push(currentGroup);
            currentGroup = null;
          }
          // Add as a special group
          messagesBySender.push({
            isAgentJoinedMessage: true,
            message: message.message,
          });
          return;
        }

        const isMessageFromSelf = isSelfMessage(message, authId, salesforceEnabled);
        const newSenderInfo = getSenderInfo(isMessageFromSelf, chatUsers, message);
        
        if (!currentGroup 
          || currentGroup.isFromSelf !== isMessageFromSelf
          || (!isMessageFromSelf && currentGroup.senderInfo?.label !== newSenderInfo?.label)
        ) {
          // Start new group
          if (currentGroup) messagesBySender.push(currentGroup);
          currentGroup = {
            isFromSelf: isMessageFromSelf,
            messages: [message],
            senderInfo: getSenderInfo(isMessageFromSelf, chatUsers, message),
          };
        } else {
          currentGroup.messages.push(message);
        }
      });
      if (currentGroup) messagesBySender.push(currentGroup);

      // Calculate date label
      const dateLabel = moment(dateKey).calendar(dateLocaleLabel);
      const shouldHideDatePill = firstMessageDate === dateKey;

      return {
        dateKey,
        items: messagesBySender,
        dateLabel,
        shouldHideDatePill,
      };
    });

    return result;
  }, [items, translated, firstMessageDate, authId, salesforceEnabled, chatUsers, hasAgentJoined, agentJoinedAt, agentJoinedMessage]);

  const getMessageText = (message) => {
    return message.message || message.text || message.messageText || "";
  };

  // Keep for future enhancement if need message timestamp
  const formatTimestamp = (message) => {
    const date = message.createdOn || message.timestamp;
    if (!date) return "";
  
    const localDate = moment(date).local();
    return localDate
      ? `${moment(localDate).locale(locale).format("h:mm a").replace("pm", "p.m").replace("am", "a.m")}`
      : '';
  };

  const buildMetaContent = useCallback((senderGroup) => {
    // TODO: Commented out but keep for future enhancement if need message status
    // const messageStatus = message.status || MESSAGE_STATUS.SENT;
    // const messageStatusHTML = <>
    //   {messageStatus === MESSAGE_STATUS.SENT && (
    //     <Icon className="message-status-indicator">check</Icon>
    //   )}
    //   {messageStatus === MESSAGE_STATUS.DELIVERED && (
    //     <Icon className="message-status-indicator">done_all</Icon>
    //   )}
    //   {messageStatus === MESSAGE_STATUS.READ && (
    //     <Icon className="message-status-indicator message-status-indicator--read">done_all</Icon>
    //   )}
    // </>;

    return (
      <div className="support-message-group-meta">
        <Typography variant="body2" component="span" className="chat-message-agent-name">
          {senderGroup.senderInfo?.label}
        </Typography>
      </div>
    );
  }, []);

  const buildMessageContent = useCallback((message) => {
    const messageText = getMessageText(message);

    // Don't render empty messages
    if (!messageText.trim()) {
      return <></>;
    }

    return (
      <Typography variant="body1" component="div">
        {linkifyText(messageText)}
      </Typography>
    );
  }, []);

  return (
    <>
      {groupedMessages.map((group, dateIndex) => (
        <div key={`date-group-${group.dateKey}`} className="chat-thread-day">
          {!group.shouldHideDatePill && (
            <span
              className="chat-thread-system-label"
              key={`date-pill-${group.dateKey}`}
            >
              {group.dateLabel}
            </span>
          )}
          
          {group.items.map((senderGroup, groupIndex) => {
            // Handle "agent joined" pseudo-message
            if (senderGroup.isAgentJoinedMessage) {
              return (
                <div className="chat-thread-started" key={`agent-joined-${group.dateKey}-${groupIndex}`}>
                  <span className="chat-thread-system-label">
                    {senderGroup.message}
                  </span>
                </div>
              );
            }

            return (
              <MessageBubbleGroup
                key={`support-message-group-${group.dateKey}-${groupIndex}`}
                isIncomingMessage={!senderGroup.isFromSelf}
                isShowAvatar={!senderGroup.isFromSelf}
                avatarUrl="/assets/icons/chat/chat-support-icon.png"
                wrapperCssClasses="chat-type-support"
                isShowMeta={!senderGroup.isFromSelf && senderGroup.senderInfo !== null}
                metaChildren={(!senderGroup.isFromSelf && senderGroup.senderInfo)
                  ? buildMetaContent(senderGroup)
                  : null}
              >
                {senderGroup.messages.map((message, messageIndex) => (
                  <MessageBubble
                    key={`support-message-bubble-${groupIndex}-${messageIndex}`}
                    messageContent={buildMessageContent(message)}
                  />
                ))}
              </MessageBubbleGroup>
            );
          })}
        </div>
      ))}
    </>
  );
};

SupportChatMessages.propTypes = {
  items: PropTypes.array,
  authId: PropTypes.string.isRequired,
  salesforceEnabled: PropTypes.bool,
  translated: PropTypes.object,
  chatUsers: PropTypes.array,
  hasAgentJoined: PropTypes.bool,
  agentJoinedAt: PropTypes.number,
  agentName: PropTypes.string,
  agentJoinedMessage: PropTypes.string,
};

export default React.memo(SupportChatMessages);